/**
 * @module components/Filters/FilterPanel
 * @description Panel de filtros para la tabla de controles ISO 27002.
 *
 * Permite filtrar los controles por estado, prioridad, categoría y fecha límite.
 * Los filtros se aplican en tiempo real sin recarga de página. Se puede limpiar
 * todos los filtros con el botón "Limpiar Filtros".
 *
 * Las opciones de filtro se calculan dinámicamente desde los valores únicos
 * presentes en los controles cargados (no hardcodeadas).
 *
 * @see DataContext.filter y DataContext.setFilter para el estado de filtros
 * @see GlobalSidebar que renderiza este panel en la sección dinámica de ISO 27002
 */
import React from 'react';
import { X, Filter, Calendar, Tag } from 'lucide-react';
import { useData } from '../../contexts/DataContext';

export function FilterPanel() {
  const { filter, setFilter, controls } = useData();

  // Get unique values for filter options
  const allStatuses = [...new Set(controls.map(c => c.status))];
  const allPriorities = [...new Set(controls.map(c => c.priority))];
  const allCategories = [...new Set(controls.map(c => c.category))];
  const allAssignees = [...new Set(controls
    .filter(c => (c.assignedReferents || []).length > 0)
    .flatMap(c => (c.assignedReferents || []).map(r => r.name)))];

  const updateFilter = (key: keyof typeof filter, value: any) => {
    setFilter({ ...filter, [key]: value });
  };

  const toggleArrayFilter = (key: 'status' | 'priority' | 'assignee' | 'category', value: string) => {
    const currentArray = filter[key] as string[];
    const newArray = currentArray.includes(value)
      ? currentArray.filter(item => item !== value)
      : [...currentArray, value];
    updateFilter(key, newArray);
  };

  const clearAllFilters = () => {
    setFilter({
      status: [],
      priority: [],
      assignee: [],
      category: [],
      dateRange: { start: '', end: '' }
    });
  };

  const hasActiveFilters = 
    filter.status.length > 0 ||
    filter.priority.length > 0 ||
    filter.assignee.length > 0 ||
    filter.category.length > 0 ||
    filter.dateRange.start !== '' ||
    filter.dateRange.end !== '';

  const getStatusText = (status: string) => {
    switch (status) {
      case 'completed': return 'Completado';
      case 'in_progress': return 'En Progreso';
      case 'review': return 'En Revisión';
      case 'not_started': return 'No Iniciado';
      default: return 'No Conforme';
    }
  };

  const getPriorityText = (priority: string) => {
    switch (priority) {
      case 'critical': return 'Crítica';
      case 'high': return 'Alta';
      case 'medium': return 'Media';
      default: return 'Baja';
    }
  };

  return (
    <div className="p-4 space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-2">
          <Filter className="w-5 h-5 text-gray-600 dark:text-gray-300" />
          <h3 className="font-medium text-gray-900 dark:text-white">Filtros</h3>
        </div>
        {hasActiveFilters && (
          <button
            onClick={clearAllFilters}
            className="text-sm text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300"
          >
            Limpiar todo
          </button>
        )}
      </div>

      {/* Estado */}
      <div>
        <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
          Estado
        </h4>
        <div className="space-y-1">
          {allStatuses.map((status) => (
            <label key={status} className="flex items-center space-x-2 cursor-pointer">
              <input
                type="checkbox"
                checked={filter.status.includes(status)}
                onChange={() => toggleArrayFilter('status', status)}
                className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
              />
              <span className="text-sm text-gray-700 dark:text-gray-300">
                {getStatusText(status)}
              </span>
            </label>
          ))}
        </div>
      </div>

      {/* Prioridad */}
      <div>
        <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
          Prioridad
        </h4>
        <div className="space-y-1">
          {allPriorities.map((priority) => (
            <label key={priority} className="flex items-center space-x-2 cursor-pointer">
              <input
                type="checkbox"
                checked={filter.priority.includes(priority)}
                onChange={() => toggleArrayFilter('priority', priority)}
                className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
              />
              <span className="text-sm text-gray-700 dark:text-gray-300">
                {getPriorityText(priority)}
              </span>
            </label>
          ))}
        </div>
      </div>

      {/* Categoría */}
      <div>
        <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2 flex items-center justify-between">
          <span>Categoría</span>
          {allCategories.length > 0 && (
            <span className="text-xs text-gray-500 dark:text-gray-400">
              {filter.category.length > 0 ? `${filter.category.length}/${allCategories.length}` : allCategories.length}
            </span>
          )}
        </h4>
        <div className="space-y-1 max-h-48 overflow-y-auto pr-1 scrollbar-thin scrollbar-thumb-gray-400 dark:scrollbar-thumb-gray-600">
          {allCategories.map((category) => (
            <label key={category} className="flex items-center space-x-2 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-800 rounded px-1 py-0.5 transition-colors">
              <input
                type="checkbox"
                checked={filter.category.includes(category)}
                onChange={() => toggleArrayFilter('category', category)}
                className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
              />
              <span className="text-sm text-gray-700 dark:text-gray-300 truncate" title={category}>
                {category}
              </span>
            </label>
          ))}
        </div>
      </div>

      {/* Responsables */}
      <div>
        <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2 flex items-center justify-between">
          <span>Responsables</span>
          {allAssignees.length > 0 && (
            <span className="text-xs text-gray-500 dark:text-gray-400">
              {filter.assignee.length > 0 ? `${filter.assignee.length}/${allAssignees.length}` : allAssignees.length}
            </span>
          )}
        </h4>
        <div className="space-y-1 max-h-48 overflow-y-auto pr-1 scrollbar-thin scrollbar-thumb-gray-400 dark:scrollbar-thumb-gray-600">
          {allAssignees.map((assignee) => (
            <label key={assignee} className="flex items-center space-x-2 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-800 rounded px-1 py-0.5 transition-colors">
              <input
                type="checkbox"
                checked={filter.assignee.includes(assignee)}
                onChange={() => toggleArrayFilter('assignee', assignee)}
                className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
              />
              <span className="text-sm text-gray-700 dark:text-gray-300">
                {assignee}
              </span>
            </label>
          ))}
        </div>
      </div>

      {/* Rango de fechas */}
      <div>
        <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
          <Calendar className="w-4 h-4 inline-block mr-1" />
          Fecha límite
        </h4>
        <div className="space-y-2">
          <input
            type="date"
            value={filter.dateRange.start}
            onChange={(e) => updateFilter('dateRange', { ...filter.dateRange, start: e.target.value })}
            className="w-full text-sm border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
            placeholder="Fecha inicio"
          />
          <input
            type="date"
            value={filter.dateRange.end}
            onChange={(e) => updateFilter('dateRange', { ...filter.dateRange, end: e.target.value })}
            className="w-full text-sm border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
            placeholder="Fecha fin"
          />
        </div>
      </div>

      {/* Filtros activos */}
      {hasActiveFilters && (
        <div>
          <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Filtros activos
          </h4>
          <div className="flex flex-wrap gap-2">
            {filter.status.map((status) => (
              <span 
                key={`status-${status}`}
                className="inline-flex items-center px-2 py-1 rounded-full text-xs bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300"
              >
                {getStatusText(status)}
                <button
                  onClick={() => toggleArrayFilter('status', status)}
                  className="ml-1 hover:text-blue-600"
                >
                  <X className="w-3 h-3" />
                </button>
              </span>
            ))}
            {filter.priority.map((priority) => (
              <span 
                key={`priority-${priority}`}
                className="inline-flex items-center px-2 py-1 rounded-full text-xs bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-300"
              >
                {getPriorityText(priority)}
                <button
                  onClick={() => toggleArrayFilter('priority', priority)}
                  className="ml-1 hover:text-orange-600"
                >
                  <X className="w-3 h-3" />
                </button>
              </span>
            ))}
            {filter.category.map((category) => (
              <span 
                key={`category-${category}`}
                className="inline-flex items-center px-2 py-1 rounded-full text-xs bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300"
              >
                <Tag className="w-3 h-3 mr-1" />
                {category.length > 15 ? `${category.substring(0, 15)}...` : category}
                <button
                  onClick={() => toggleArrayFilter('category', category)}
                  className="ml-1 hover:text-green-600"
                >
                  <X className="w-3 h-3" />
                </button>
              </span>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}